package com.eladfinish.bible.contest.screens.main

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.compose.LocalLifecycleOwner
import androidx.navigation.NavController
import com.eladfinish.bible.contest.LocalAppActions
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.analytics.crashlytics.AppCrashlytics
import com.eladfinish.bible.contest.cannot_open_link
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetEntryPoint
import com.eladfinish.bible.contest.navigation.BrowseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.CompleteChapterScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.CompleteVerseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.LexicographicalSortScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.MikudScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.MiscScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.MultipleChoiceQuestionsScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.NamesScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.OpenQuestionsScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.PairMatchingScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.PastQuizzesNavigationDestination
import com.eladfinish.bible.contest.navigation.PyramidScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.RandomVerseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.RootNavigationDestination
import com.eladfinish.bible.contest.navigation.SearchScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.SettingsRootNavigationDestination
import com.eladfinish.bible.contest.navigation.VerseWidgetScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.screenKey
import com.eladfinish.bible.contest.platform.isDesktop
import com.eladfinish.bible.contest.platform.isWeb
import com.eladfinish.bible.contest.screens.main.data.ButtonType
import com.eladfinish.bible.contest.screens.main.data.ItemMode
import com.eladfinish.bible.contest.screens.main.pastQuizzesSheet.PastQuizzesSheet
import com.eladfinish.bible.contest.screens.main.pastQuizzesSheet.PastQuizzesSource
import com.eladfinish.bible.contest.screens.main.ui.MainScreen
import com.eladfinish.bible.contest.screens.main.verseWidgetSheet.VerseWidgetSheet
import com.eladfinish.bible.contest.screens.pastQuizzes.isAppInDarkTheme
import com.eladfinish.bible.contest.screens.recordings.RECORDINGS_URL
import com.eladfinish.bible.contest.ui.common.rememberSafeUriHandler
import com.eladfinish.bible.contest.ui.menus.actions.MainToolbarAction
import com.eladfinish.bible.contest.ui.menus.viewModels.ToolbarActionBusViewModel
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import io.github.aakira.napier.Napier
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun MainScreenWrapper(
    mainScreenStore: MainScreenStore,
    toolbarActionBus: ToolbarActionBusViewModel,
    navController: NavController,
    onHebrewGenderConfirmed: (HebrewGender) -> Unit,
    showToast: (StringDesc) -> Unit,
    logEvent: (String) -> Unit,
) {
    val appActions = LocalAppActions.current
    val isDarkTheme = isAppInDarkTheme()
    val currentShowToast by rememberUpdatedState(showToast)

    val safeUriHandler = rememberSafeUriHandler { throwable, _ ->
        AppCrashlytics.logException(throwable)
        currentShowToast(SharedRes.strings.cannot_open_link.desc())
    }

    MainToolbarBindings(
        onAction = mainScreenStore::dispatch,
        toolbarActionBus = toolbarActionBus
    )

    // Runs at every return to the main menu: the root destination re-enters composition.
    LaunchedEffect(Unit) {
        mainScreenStore.onMainMenuShown()
    }

    MainScreenSideEffectsCollector(
        effects = mainScreenStore.observeSideEffect(),
        appActions = appActions,
        currentShowToast = currentShowToast,
    )

    // The home button and the "what's new" sheet open it the same way, so the kill switch, the
    // theme, the gender and the embed parameters apply to both.
    fun openPastQuizzes(source: PastQuizzesSource) {
        // Web has no WebView for a remote page and desktop has none at all: use the browser.
        if (isWeb || isDesktop) {
            mainScreenStore.onPastQuizzesOpened(inApp = false, source = source)
            safeUriHandler.openUri(mainScreenStore.pastQuizzesUrl(isDarkTheme = isDarkTheme))
        } else {
            mainScreenStore.onPastQuizzesOpened(inApp = true, source = source)
            navController.navigate(PastQuizzesNavigationDestination())
        }
    }

    // The home tile and the "what's new" sheet open the widget's settings the same way; only the
    // entry point differs, to tell which way of finding the feature works.
    fun openVerseWidget(entryPoint: VerseWidgetEntryPoint) {
        navController.navigate(VerseWidgetScreenNavigationDestination(entryPoint))
    }

    PastQuizzesSheetHost(
        mainScreenStore = mainScreenStore,
        onCtaClicked = { openPastQuizzes(PastQuizzesSource.WHATS_NEW_SHEET) },
    )

    VerseWidgetSheetHost(
        mainScreenStore = mainScreenStore,
        onCtaClicked = { openVerseWidget(VerseWidgetEntryPoint.WHATS_NEW_SHEET) },
    )

    MainScreen(
        mainScreenStore = mainScreenStore,
        onClick = {
            when (it) {
                ButtonType.UNKNOWN -> Unit
                ButtonType.ABOUT -> Unit
                ButtonType.HELP -> Unit
                ButtonType.REORDER -> Unit

                ButtonType.EXIT -> {
                    appActions.exitApp()
                }

                ButtonType.NAMES -> {
                    navController.navigate(NamesScreenNavigationDestination)
                }

                ButtonType.SETTINGS -> {
                    navController.navigate(SettingsRootNavigationDestination)
                }

                ButtonType.PYRAMID_CROSSWORD -> {
                    navController.navigate(PyramidScreenNavigationDestination)
                }

                ButtonType.MISC -> {
                    navController.navigate(MiscScreenNavigationDestination)
                }

                ButtonType.RANDOM_VERSE -> {
                    navController.navigate(RandomVerseScreenNavigationDestination)
                }

                ButtonType.VERSE_WIDGET -> {
                    openVerseWidget(VerseWidgetEntryPoint.HOME_TILE)
                }

                ButtonType.COMPLETE_VERSE -> {
                    navController.navigate(CompleteVerseScreenNavigationDestination)
                }

                ButtonType.SEARCH -> {
                    navController.navigate(SearchScreenNavigationDestination())
                }

                ButtonType.COMPLETE_CHAPTER -> {
                    navController.navigate(CompleteChapterScreenNavigationDestination)
                }

                ButtonType.PAIR_MATCHING -> {
                    navController.navigate(PairMatchingScreenNavigationDestination)
                }

                ButtonType.MULTIPLE_CHOICE_QUESTIONS -> {
                    navController.navigate(MultipleChoiceQuestionsScreenNavigationDestination())
                }

                ButtonType.OPEN_QUESTIONS -> {
                    navController.navigate(OpenQuestionsScreenNavigationDestination())
                }

                ButtonType.BROWSE -> {
                    navController.navigate(BrowseScreenNavigationDestination())
                }

                ButtonType.MIKUD -> {
                    navController.navigate(MikudScreenNavigationDestination)
                }

                ButtonType.RECORDINGS -> {
                    safeUriHandler.openUri(RECORDINGS_URL)
                }

                ButtonType.TELEGRAM_CHANNEL -> {
                    safeUriHandler.openUri(mainScreenStore.telegramChannelUrl)
                }

                ButtonType.LEXICOGRAPHICAL_SORT -> {
                    navController.navigate(LexicographicalSortScreenNavigationDestination())
                }

                ButtonType.PAST_QUIZZES -> {
                    openPastQuizzes(PastQuizzesSource.HOME_BUTTON)
                }
            }
        },
        onLongClick = {
            Napier.d("ButtonType $it was long clicked")
            when (it) {
                ButtonType.MIKUD -> appActions.onMikudLongClicked()
                ButtonType.SETTINGS -> appActions.onSettingsLongClicked()
                ButtonType.BROWSE -> appActions.onBrowseLongClicked()
                ButtonType.ABOUT -> appActions.onAboutLongClicked()
                else -> Unit
            }
        },
        onHebrewGenderConfirmed = onHebrewGenderConfirmed,
        showToast = showToast,
        logEvent = logEvent,
    )
}

/**
 * The "what's new" sheet of חידונים אמיתיים: once the store offers it, it waits until the home
 * screen has been idle for a moment and then shows (see [ShowWhenHomeIsIdle]).
 */
@Composable
private fun PastQuizzesSheetHost(
    mainScreenStore: MainScreenStore,
    onCtaClicked: () -> Unit,
) {
    val state by mainScreenStore.observeState().collectAsState()

    ShowWhenHomeIsIdle(state = state, isOffered = state.isPastQuizzesSheetOffered) {
        mainScreenStore.dispatch(MainScreenAction.ShowPastQuizzesSheet)
    }

    if (state.isPastQuizzesSheetVisible) {
        PastQuizzesSheet(
            onDismissed = { method ->
                mainScreenStore.dispatch(MainScreenAction.PastQuizzesSheetDismissed(method = method))
            },
            onCtaClicked = {
                mainScreenStore.dispatch(MainScreenAction.PastQuizzesSheetCtaClicked)
                onCtaClicked()
            },
        )
    }
}

/** The "what's new" sheet of ווידג'ט פסוק מתחלף, shown the same way as [PastQuizzesSheetHost]'s. */
@Composable
private fun VerseWidgetSheetHost(
    mainScreenStore: MainScreenStore,
    onCtaClicked: () -> Unit,
) {
    val state by mainScreenStore.observeState().collectAsState()

    ShowWhenHomeIsIdle(state = state, isOffered = state.isVerseWidgetSheetOffered) {
        mainScreenStore.dispatch(MainScreenAction.ShowVerseWidgetSheet)
    }

    if (state.isVerseWidgetSheetVisible) {
        VerseWidgetSheet(
            onDismissed = { method ->
                mainScreenStore.dispatch(MainScreenAction.VerseWidgetSheetDismissed(method = method))
            },
            onCtaClicked = {
                mainScreenStore.dispatch(MainScreenAction.VerseWidgetSheetCtaClicked)
                onCtaClicked()
            },
        )
    }
}

/**
 * Calls [onIdle] once the home screen has been idle for [HOME_SHEET_IDLE_DELAY] while a sheet is
 * [isOffered]: on screen and resumed, not reordering, no dialog open. Anything that comes up in the
 * meantime restarts the wait.
 */
@Composable
private fun ShowWhenHomeIsIdle(state: MainScreenState, isOffered: Boolean, onIdle: () -> Unit) {
    val lifecycleState by LocalLifecycleOwner.current.lifecycle.currentStateFlow.collectAsState()
    val currentOnIdle by rememberUpdatedState(onIdle)

    val isHomeIdle = isOffered &&
        state.itemMode == ItemMode.REGULAR &&
        state.dialogState == MainScreenState.DialogState.None &&
        state.hebrewGenderDialogAlreadyShown &&
        lifecycleState.isAtLeast(Lifecycle.State.RESUMED)

    LaunchedEffect(isHomeIdle) {
        if (!isHomeIdle) return@LaunchedEffect
        delay(HOME_SHEET_IDLE_DELAY)
        currentOnIdle()
    }
}

private val HOME_SHEET_IDLE_DELAY = 1500.milliseconds

@Composable
private fun MainToolbarBindings(
    onAction: (MainScreenAction) -> Unit,
    toolbarActionBus: ToolbarActionBusViewModel,
) {
    val screenKey = RootNavigationDestination::class.screenKey

    val onDispatch by rememberUpdatedState<(MainToolbarAction) -> Unit> { action ->
        onAction(MainScreenAction.ToolbarClicked(toolbarAction = action))
    }

    LaunchedEffect(screenKey) {
        toolbarActionBus.setActiveScreenKey(screenKey)
        toolbarActionBus.events.collect { event ->
            if (event.screenKey != screenKey) return@collect
            val action = event.action as? MainToolbarAction ?: return@collect
            onDispatch(action)
        }
    }
}

@Composable
fun MainScreenSideEffectsCollector(
    effects: Flow<MainScreenEffect>,
    appActions: com.eladfinish.bible.contest.AppActions,
    currentShowToast: (StringDesc) -> Unit,
) {
    LaunchedEffect(Unit) {
        effects.collect { handleMainScreenEffect(it, appActions, currentShowToast) }
    }
}

fun handleMainScreenEffect(
    effect: MainScreenEffect,
    appActions: com.eladfinish.bible.contest.AppActions,
    showToast: (StringDesc) -> Unit,
) {
    when (effect) {
        MainScreenEffect.CheckUpdate -> appActions.checkUpdate()
        MainScreenEffect.RateApp -> appActions.rateApp()
        MainScreenEffect.Upgrade -> appActions.upgrade()
        MainScreenEffect.ShowSupportAd -> appActions.showAd()
        MainScreenEffect.ShowRewardedAd -> appActions.showRewardedAd()
        MainScreenEffect.StartRemoveAdsPurchase -> appActions.startRemoveAdsPurchase()
        MainScreenEffect.RestorePurchase -> appActions.restorePurchase()
        MainScreenEffect.ConsumeTestPurchase -> appActions.consumeTestPurchase()
        is MainScreenEffect.ShowToast -> showToast(effect.message)
    }
}
